import { createSlice } from '@reduxjs/toolkit';
import { getProblemDetail } from 'api/v1Helpers';
import { notify } from 'components/Common/Toast';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import * as stripeApi from 'services/stripeApi';
import type { StripeParishStatus } from 'types/stripe';

interface StripeOnboardingState {
    status: StripeParishStatus | null;
    loading: boolean;
    linkLoading: boolean;
    error: string | null;
    /** Parish id of the latest in-flight status request (race guard). */
    inflightParishId: string | null;
}

const initialState: StripeOnboardingState = {
    status: null,
    loading: false,
    linkLoading: false,
    error: null,
    inflightParishId: null,
};

export const fetchParishStripeStatus = createAsyncThunkWrapper<
    { status: StripeParishStatus; parishId: string } | null,
    { parishId: string }
>(
    'stripeOnboarding/fetchStatus',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        try {
            const status = await stripeApi.fetchStripeStatus(parishId);
            if (parishSelectionMoved(getState, parishId)) return null;
            return { status, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load Stripe status' }
);

/**
 * Mints a Connect Express onboarding link and returns the URL.
 * Caller redirects to Stripe; do not open a second window.
 */
export const startStripeOnboarding = createAsyncThunkWrapper<{ url: string; parishId: string } | null, { parishId: string }>(
    'stripeOnboarding/startOnboarding',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        try {
            const link = await stripeApi.createStripeOnboardingLink(parishId);
            if (parishSelectionMoved(getState, parishId)) return null;
            if (!link.url) {
                notify('Stripe did not return an onboarding URL', 'error');
                return null;
            }
            return { url: link.url, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to start Stripe onboarding' }
);

const stripeOnboardingSlice = createSlice({
    name: 'stripeOnboarding',
    initialState,
    reducers: {
        clearStripeOnboarding(state) {
            state.status = null;
            state.error = null;
            state.inflightParishId = null;
            state.loading = false;
            state.linkLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchParishStripeStatus.pending, (state, action) => {
            const nextParishId = action.meta.arg.parishId;
            state.loading = true;
            state.error = null;
            // Soft refresh (same parish): keep prior status to avoid flicker.
            // Parish switch: drop status so another parish's chips cannot paint.
            if (state.inflightParishId !== nextParishId) {
                state.status = null;
            }
            state.inflightParishId = nextParishId;
        });
        builder.addCase(fetchParishStripeStatus.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId) {
                    state.loading = false;
                }
                return;
            }
            const { status, parishId } = action.payload;
            if (parishId !== state.inflightParishId) return;
            state.loading = false;
            state.status = status;
        });
        builder.addCase(fetchParishStripeStatus.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId) {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to load Stripe status';
            }
        });

        builder.addCase(startStripeOnboarding.pending, (state) => {
            state.linkLoading = true;
        });
        builder.addCase(startStripeOnboarding.fulfilled, (state) => {
            state.linkLoading = false;
        });
        builder.addCase(startStripeOnboarding.rejected, (state) => {
            state.linkLoading = false;
        });
    },
});

export const { clearStripeOnboarding } = stripeOnboardingSlice.actions;
export default stripeOnboardingSlice.reducer;
